import { COMPONENT_MAP, TAG_NAMES } from '@utils/pageBuilderHelper';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { useCallback, useEffect, useMemo } from 'react';
import {
  usePublicEventDetailsBySlug,
  usePublicEventPageDetailsBySlug,
} from '@utils/apiHooks';

import LoadingFallBack from '@routes/layouts/components/LoadingFallback';
import { MODE } from '@utils/constants';
import { getMode } from '@store/slices/configSlice';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useInitCountdownWhenPresent, useViewAnalyzer } from '@utils/hooks';
import { adminEventsRouteFormat } from '@constants/paths';
import { getEventDetails } from '@store/slices/eventSlice';
import { MetaTags } from '@components/MetaTags';
import { getImageSrc } from '@utils/fn';

const EventPageRoot = styled.div`
  width: 100vw;
`;

const EventPage = () => {
  const mode = useSelector(getMode);
  const { eventSlug, pageSlug } = useParams();
  const view = useViewAnalyzer();
  const navigate = useNavigate();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  // Trigger counter
  useInitCountdownWhenPresent();

  const {
    data: eventDetails,
    isSuccess,
    isError: isEventError,
  } = usePublicEventDetailsBySlug({
    eventSlug: eventSlug as string,
    enabled: view === MODE.EVENT && String(eventSlug as string)?.length > 0,
  });

  const {
    data: pageDetails,
    isLoading: isPublicEventPageDetailsLoading,
    isError: isPageError,
  } = usePublicEventPageDetailsBySlug({
    eventId: eventDetails?.[0]?.id ?? '',
    pageSlug: pageSlug as string,
    enabled:
      view === MODE.EVENT && isSuccess && (pageSlug as string)?.length > 0,
  });

  // Handle 404 redirects
  useEffect(() => {
    if (view === MODE.EVENT) {
      // Case 1: Event exists but page not found
      if (isSuccess && isPageError && pageSlug) {
        navigate(adminEventsRouteFormat(eventSlug as string, ''));
        return;
      }

      // Case 2: Event slug exists but ID not found (for example in partner/:id route)
      if (isSuccess && pageSlug && !pageDetails) {
        // Check common patterns like partner/ID, speaker/ID, etc.
        const partnerMatch = pageSlug.match(/^partner(s)?\/(\d+)$/);
        const speakerMatch = pageSlug.match(/^speaker(s)?\/(\d+)$/);

        if ((partnerMatch || speakerMatch) && !pageDetails) {
          navigate(adminEventsRouteFormat(eventSlug as string, ''));
          return;
        }
      }

      // Case 3: Language mismatch (e.g., English slug but German event)
      if (isSuccess && pageSlug && sluggedEvent?.language) {
        // Map of English page slugs to German equivalents
        const languageSpecificSlugs: Record<
          string,
          Record<string, string[]>
        > = {
          en: {
            'who-why': ['who-why', 'who-and-why'],
            speakers: ['speakers', 'speaker'],
            partners: ['partners', 'partner'],
            agenda: ['agenda'],
            contact: ['contact'],
            faq: ['faq', 'faqs'],
          },
          de: {
            'wer-warum': ['wer-warum', 'wer-und-warum'],
            sprecher: ['sprecher'],
            partners: ['partners', 'partner'],
            agenda: ['agenda', 'terminplan'],
            kontakt: ['kontakt', 'contact'],
            faq: ['faq', 'faqs'],
          },
        };

        // Check if the page slug exists in the wrong language
        if (sluggedEvent.language === 'en') {
          // Check if the current page slug is a German slug
          for (const [deSlug, _] of Object.entries(languageSpecificSlugs.de)) {
            if (pageSlug === deSlug || pageSlug.startsWith(`${deSlug}/`)) {
              navigate(adminEventsRouteFormat(eventSlug as string, ''));
              return;
            }
          }
        } else if (sluggedEvent.language === 'de') {
          // Check if the current page slug is an English slug
          for (const [enSlug, _] of Object.entries(languageSpecificSlugs.en)) {
            if (pageSlug === enSlug || pageSlug.startsWith(`${enSlug}/`)) {
              navigate(adminEventsRouteFormat(eventSlug as string, ''));
              return;
            }
          }
        }
      }

      // Case 4: Non-dynamic page accessed with dynamic content disabled
      if (isSuccess && pageSlug && (pageDetails as any)?.dynamic === 0) {
        navigate(adminEventsRouteFormat(eventSlug as string, ''));
        
      }
    }
  }, [
    view,
    isSuccess,
    isPageError,
    eventSlug,
    pageSlug,
    pageDetails,
    navigate,
    sluggedEvent?.language,
  ]);

  const replaceWithComponent = useCallback((html: string) => {
    const options: HTMLReactParserOptions = {
      replace: domNode => {
        const element = domNode as Element;
        const tagName = element.tagName;
        const attributes = (domNode as Element)?.attribs;
        if (!tagName || !attributes) return;
        const mlsid = attributes.mlsid;

        if (tagName === TAG_NAMES.DIV && COMPONENT_MAP[mlsid]) {
          const { component: Component, getProps } = COMPONENT_MAP[mlsid];
          const props = getProps(attributes);
          return <Component {...props} />;
        }

        if (
          tagName === TAG_NAMES.BUTTON &&
          attributes?.class === 'inbound-button'
        ) {
          const { component: Component, getProps } = COMPONENT_MAP[mlsid];
          const props = getProps(attributes);
          return <Component {...props} />;
        }
      },
    };
    return parse(html, options);
  }, []);

  const modifiedContent = useMemo(
    () => replaceWithComponent(pageDetails?.content_html ?? ''),
    [pageDetails?.content_html, replaceWithComponent]
  );

  if (mode !== MODE.EVENT) return null;

  return (
    <EventPageRoot>
      <MetaTags
        title={pageDetails?.slug ?? ''}
        description={pageDetails?.slug ?? sluggedEvent?.name}
        keywords={`${sluggedEvent?.slug}, ${pageDetails?.slug ?? sluggedEvent?.name}`}
        image={getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)}
        url={window.location.href}
        twitterCardType="summary"
        type="product"
      />
      {isPublicEventPageDetailsLoading ? (
        <LoadingFallBack />
      ) : (
        <>{modifiedContent}</>
      )}
    </EventPageRoot>
  );
};

export default EventPage;
